import React, {memo, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import Card from "../../../components/Card";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {Input} from "@material-tailwind/react/components/Input";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {getError, hasError} from "../../../hooks/errors";
import {clear, validateData} from "../../../reducers/validationReducer";
import {InputType} from "../../../types/InputType";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useNavigate, useParams} from "react-router-dom";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import FormSkeleton from "../../../components/Skeletons/FormSkeleton";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useErrorHandler} from "react-error-boundary";
import {User} from "../../../types/User";
import {RolesShortResponse} from "../../../types/Responses/RolesAndPermissions";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import DropdownInput from "../../../components/DropdownInput";
import OutlineInput from "../../../components/OutlineInput";
import {UserListResponse} from "../../../types/Responses/UsersResponse";
import BrokerAvatar from "../../../components/BrokerAvatar";
import CountryAvatar from "../../../components/CountryAvatar";
import {useTranslation} from "react-i18next";
import {useUserContext} from "../../../types/contexts/UserContext";
import {setTitle} from "../../../hooks/setTitle";
import {Editor} from "@tinymce/tinymce-react";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {removeDataTableRequest} from "../../../reducers/dataTableSearch";
import {CountriesShortResponse} from "../../../types/Responses/CountriesResponse";
import {AllRegionsResponse} from "../../../types/Responses/RegionsResponse";
import {setSidebarTitle} from "../../../reducers/sidebar";

const UserForm = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const user = useUserContext();
    const system = useSystemContext();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();

    const [listCountries, setListCountries] = useState<CountriesShortResponse>();
    const [allRegions, setAllRegions] = useState<AllRegionsResponse>();

    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [domain, setDomain] = useState<string>();
    const [ips, setIps] = useState<string>();
    const [regions, setRegions] = useState<number[]>([]);
    const [countries, setCountries] = useState<number[]>([]);
    const [password, setPassword] = useState<string>();
    const [status, setStatus] = useState<number>(1);
    const [language, setLanguage] = useState<number>(1);
    const [baseCurrency, setBaseCurrency] = useState<number>(5);
    const [role, setRole] = useState<number>(1);
    const [timezone, setTimezone] = useState<number>();
    const [parent, setParent] = useState<number | null>(null);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [notifyURL, setNotifyURL] = useState<string>("");
    const [redirectURL, setRedirectURL] = useState<string>("");
    const [europeWire, setEuropeWire] = useState<string>("");
    const [nonEuropeWire, setNonEuropeWire] = useState<string>("");

    const [passwordType, setPasswordType] = useState<'text' | 'password'>('password')
    const [allowedRoles, setAllowedRoles] = useState<RolesShortResponse>();
    const [roles, setRoles] = useState<RolesShortResponse>();
    const [users, setUsers] = useState<UserListResponse>();
    const [isInProgress, setInProgress] = useState<boolean>(false);
    const [validated, setValidated] = useState<boolean>(false);
    const [formIsReady, setFormIsReady] = useState<boolean>(false);
    const [isRequestSent, setIsRequestSent] = useState<boolean>(false);
    const swiftEditorRef = useRef<{
        getContent: () => string
    } | null>(null);
    const sepaEditorRef = useRef<{
        getContent: () => string
    } | null>(null);
    const dispatch = useDispatch();
    const errors = useSelector((state: RootState) => state.validation.errors);

    const getSingleUser = useCallback((rolesList: RolesShortResponse) => {
        if (!system || !system.system || !system.system.languages || !system.system.statuses || !system.system.timezones || !system.system.currencies)
            return;

        API.getUser(parseInt(id ?? '')).then((response: AxiosResponse) => {
            const userResponse = response.data.data as User
            setFirstName(userResponse.first_name);
            setLastName(userResponse.last_name);
            setEmail(userResponse.email);
            setDomain(userResponse.domain);
            setIps(userResponse.ips);
            setCountries(userResponse.countries);
            setParent(userResponse.reported && userResponse.reported.user ? parseInt(userResponse.reported.user.id) : null)
            // @ts-expect-error can be undefined
            setLanguage(system.system.languages.find(l => l.code === userResponse.language)?.id);
            // @ts-expect-error can be undefined
            setStatus(system.system.user_statuses.find(l => l.name === userResponse.status)?.id);
            // @ts-expect-error can be undefined
            setRole(rolesList.data.find(r => r.name === userResponse.roles[0])?.id)
            // @ts-expect-error can be undefined
            setTimezone(system.system.timezones.find(t => t.name === userResponse.timezone)?.id);
            // @ts-expect-error can be undefined
            setBaseCurrency(system.system.currencies.find(c => c.code === userResponse.currency)?.id);

            if (userResponse.links) {
                setRedirectURL(userResponse.links.redirect_url);
                setNotifyURL(userResponse.links.notify_url);
            }
            if (userResponse.wire) {
                setEuropeWire(userResponse.wire.europe);
                setNonEuropeWire(userResponse.wire.non_europe);
            }
            setFormIsReady(true);
            setTitle(t('admin.users.update.title', {name: userResponse.first_name + ' ' + userResponse.last_name}));
            dispatch(setSidebarTitle(t('admin.users.update.title', {name: userResponse.first_name + ' ' + userResponse.last_name})));
        }).catch(e => {
            ErrorHandler(e, `get${APILink.GET_USER.replace('$user', id?.toString() ?? '')}`, toast, handleError, setIsForbidden)
            setFormIsReady(true);
        });
    }, [system, toast, handleError, dispatch, id]);

    const sendRolesRequest = useCallback(() => {
        if (!system || !system.system || !system.system.languages)
            return;

        if (!roles) {
            API.getShortRoles().then((response: AxiosResponse) => {
                setRoles(response.data as RolesShortResponse);
                if (id) {
                    getSingleUser(response.data as RolesShortResponse);
                } else setFormIsReady(true);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_SHORT_ROLES}`, toast, handleError));
        }
    }, [toast, handleError, roles, getSingleUser, id, system]);

    const sendAllowedRolesRequest = useCallback(() => {
        if (!system || !system.system || !system.system.languages)
            return;

        if (!allowedRoles) {
            API.getAllowedRoles().then((response: AxiosResponse) => {
                setAllowedRoles(response.data as RolesShortResponse);
            }).catch(e => ErrorHandler(e, `get${APILink.GET_ALLOWED_ROLES}`, toast, handleError));
        }
    }, [toast, handleError, allowedRoles, system]);

    const sendUsersRequest = useCallback(() => {
        API.getUsersList().then((response: AxiosResponse) => {
            setUsers(response.data as UserListResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_USERS_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);

    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeDomain = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setDomain(e.currentTarget.value)
        }
    }, [setValidated, setDomain]);

    const changeIps = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setIps(e.currentTarget.value)
        }
    }, [setValidated, setIps]);

    const changePassword = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setPassword(e.currentTarget.value)
        }
    }, [setValidated, setPassword]);

    const changeStatus = useCallback((s: string | number) => {
        setStatus(typeof s === 'string' ? parseInt(s) : s);
    }, [setStatus]);

    const changeParent = useCallback((s: string | number) => {
        setParent(typeof s === 'string' ? parseInt(s) : s);
    }, [setParent]);

    const changeTimezone = useCallback((s: string | number) => {
        setTimezone(typeof s === 'string' ? parseInt(s) : s);
    }, [setTimezone]);

    const changeCurrency = useCallback((s: string | number) => {
        setBaseCurrency(typeof s === 'string' ? parseInt(s) : s);
    }, [setBaseCurrency]);

    const onNotifyUrlChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setNotifyURL(e.currentTarget.value)
        }
    }, [setValidated, setNotifyURL]);

    const onRedirectUrlChanged = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setRedirectURL(e.currentTarget.value)
        }
    }, [setValidated, setRedirectURL]);

    const changeLanguage = useCallback((s: string | number) => {
        setLanguage(typeof s === 'string' ? parseInt(s) : s);
    }, [setLanguage]);

    const changeRole = useCallback((s: string | number) => {
        setRole(typeof s === "string" ? parseInt(s) : s);
        setParent(null);
    }, [setRole, setParent]);


    const handleSuccessResponse = useCallback((response: AxiosResponse, message: JSX.Element | string) => {
        if (!user || !user.user)
            return;
        const userResponse = response.data.data as User
        if (userResponse) {
            if (user.user.id === parseInt(id as string)) {
                user.setUser(userResponse)
            }
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {message}</>, "bg-teal-500");
            navigate('/app/admin/users');
        }
    }, [toast, navigate, user]);

    const copyToClipboard = useCallback((pass: string) => {
        const el = document.createElement("textarea");
        if (el) {
            el.value = pass;
            document.body.appendChild(el);
            el.select();
            document.execCommand("copy");
            document.body.removeChild(el);
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.users.copied_password')}</>, "bg-teal-500")
        }
    }, [toast, password]);

    const generatePassword = useCallback(() => {
        const charset = "!@#$%^&*()0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ ";
        let newPassword = "";

        for (let i = 0; i < 32; i++) {
            newPassword += charset.charAt(Math.floor(Math.random() * charset.length));
        }

        setPassword(newPassword);
        setPasswordType('text');
        copyToClipboard(newPassword);
        setTimeout(function () {
            setPasswordType('password');
        }, 5000);
    }, [setPassword, copyToClipboard]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        setInProgress(false);

        if (b && !isRequestSent) {
            setIsRequestSent(true);
            let request = {
                first_name: firstName,
                last_name: lastName,
                email,
                domain,
                status_id: status,
                role_id: role,
                language_id: language,
                parent_id: parent,
                timezone_id: timezone,
                notify_url: notifyURL,
                redirect_url: redirectURL,
                sepa: !sepaEditorRef || typeof sepaEditorRef.current === 'undefined' || sepaEditorRef.current === null ? europeWire : sepaEditorRef.current?.getContent(),
                swift: !swiftEditorRef || typeof swiftEditorRef.current === 'undefined' || swiftEditorRef.current === null ? nonEuropeWire : swiftEditorRef.current?.getContent(),
                base_currency_id: baseCurrency,
                password,
                countries

            };
            if (ips)
                request = {...request, ['ips' as keyof typeof request]: ips.split(',')};
            if (id) {
                API.updateUser(parseInt(id ?? ''), request).then((response: AxiosResponse) => {
                    handleSuccessResponse(response, t('admin.users.updated.title', {name: firstName + ' ' + lastName}));
                    setIsRequestSent(false);
                }).catch(e => {
                    ErrorHandler(e, `put${APILink.GET_USER.replace('$user', id?.toString())}`, toast, handleError);
                    setIsRequestSent(false);
                });
            } else {
                API.createUser(request).then((response: AxiosResponse) => {
                    dispatch(removeDataTableRequest('/app/admin/users'));
                    handleSuccessResponse(response, t('admin.users.created.title', {name: firstName + ' ' + lastName}));
                    setIsRequestSent(false);
                }).catch(e => {
                    ErrorHandler(e, `post${APILink.GET_USERS}`, toast, handleError);
                    setIsRequestSent(false);
                });
            }

        }
    }, [password, ips, countries, firstName, isRequestSent, sepaEditorRef, swiftEditorRef, nonEuropeWire, europeWire, baseCurrency, redirectURL, notifyURL, parent, lastName, timezone, email, domain, status, role, language, toast, handleError, handleSuccessResponse, id, setValidated, setInProgress]);

    const submit = useCallback((e?: SyntheticEvent) => {
        if (e)
            e.preventDefault();
        setInProgress(true);
        const inputs = [
            {key: 'first_name', label: t('admin.users.table.first_name'), type: InputType.text, regex: '^.{3,}\\b$'},
            {key: 'last_name', label: t('admin.users.table.last_name'), type: InputType.text, regex: '^.{3,}\\b$'},
            {key: 'email', label: t('admin.users.table.email'), type: InputType.text, regex: '^.{3,}\\b$'},
            {key: 'status', label: t('admin.users.table.status'), type: InputType.text, regex: '^\\d{1,3}$'},
            {key: 'role', label: t('admin.users.role'), type: InputType.text, regex: '^\\d{1,3}$'},
            {key: 'language', label: t('admin.users.table.language'), type: InputType.text, regex: '^\\d{1,3}$'},
            {key: 'timezone', label: t('admin.users.table.timezone'), type: InputType.text, regex: '^\\d{1,3}$'},
            {key: 'currency', label: t('admin.users.table.currency'), type: InputType.text, regex: '^\\d{1,3}$'},
        ];
        let data = {
            first_name: firstName,
            last_name: lastName,
            email,
            status,
            role,
            language,
            timezone,
            currency: baseCurrency,
            domain: undefined
        };
        if (roles && roles.data.find(r => r.id === role)?.name.toLowerCase() !== 'admin') {
            inputs.push({
                key: 'domain',
                label: t('admin.users.table.domain'),
                type: InputType.text,
                regex: '^[a-zA-Z0-9-._:]{3,}\\b'
            });
            data = {
                ...data,
                ['domain' as keyof typeof data]: domain ?? ''
            };
        }
        if (roles && roles.data.find(r => r.id === role)?.name === 'Broker') {
            inputs.push({
                key: 'notify_url',
                label: t('admin.profile.notify_url'),
                type: InputType.text,
                regex: '^.{15,}$'
            });
            inputs.push({
                key: 'redirect_url',
                label: t('admin.profile.redirect_url'),
                type: InputType.text,
                regex: '^.{15,}$'
            });
            if (!swiftEditorRef || typeof swiftEditorRef.current === 'undefined' || swiftEditorRef.current === null)
                return;
            if (!sepaEditorRef || typeof sepaEditorRef.current === 'undefined' || sepaEditorRef.current === null)
                return;
            if (sepaEditorRef.current.getContent().length > 0) {
                inputs.push({
                    key: 'sepa',
                    label: t('admin.profile.sepa'),
                    type: InputType.text,
                    regex: '^(\\r\\n|\\r|\\n|.){25,}$'
                });
            }
            if (swiftEditorRef.current.getContent().length > 0) {
                inputs.push({
                    key: 'swift',
                    label: t('admin.profile.swift'),
                    type: InputType.text,
                    regex: '^(\\r\\n|\\r|\\n|.){25,}$'
                });
            }
            data = {
                ...data,
                ['notify_url' as keyof typeof data]: notifyURL,
                ['redirect_url' as keyof typeof data]: redirectURL,
                ['sepa' as keyof typeof data]: sepaEditorRef.current.getContent(),
                ['swift' as keyof typeof data]: swiftEditorRef.current.getContent()
            }
        }
        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));
    }, [dispatch, setInProgress, swiftEditorRef, sepaEditorRef, callback, baseCurrency, roles, europeWire, nonEuropeWire, redirectURL, notifyURL, firstName, lastName, email, ips, domain, status, role, language, parent, timezone]);

    const parentUsers = useCallback(() => {
        if (!users || !allowedRoles)
            return [];

        const selectedRole = allowedRoles.data.find(u => u.id === role);
        if (!selectedRole)
            return [];
        const searchRoles = allowedRoles.data.find(r => r.id === selectedRole.parent_id)?.name ?? '';
        return users.data.filter(u => u.id !== id && !u.roles?.includes('Admin') && u.roles?.includes(searchRoles)).map((u) => {
            return {id: parseInt(u.id), name: u.name, icon: <BrokerAvatar image={u.image} size="xs"/>};
        })
    }, [users, allowedRoles, role, id]);

    const sendCountriesRequest = useCallback(() => {
        API.getCountriesShortList().then((response: AxiosResponse) => {
            setListCountries(response.data as CountriesShortResponse);
            if (!id) {
                setFormIsReady(true);
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_COUNTRIES_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const sendRegionsRequest = useCallback(() => {
        API.getRegionsShortList().then((response: AxiosResponse) => {
            setAllRegions(response.data as AllRegionsResponse);
        }).catch(e => ErrorHandler(e, `get${APILink.GET_REGIONS_SHORT_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const onCountryRemove = useCallback((index: number) => {
        if (countries) {
            const newCountries = [...countries];
            if (newCountries[index]) {
                newCountries.splice(index, 1)
            }
            setCountries(newCountries)
        }
    }, [countries, setCountries]);

    const onCountryClear = useCallback(() => {
        setCountries([]);
    }, []);

    const changeRegions = useCallback((s: string | number) => {
        if (!allRegions)
            return;
        const newRegions = regions ? [...regions] : [];
        const filteredRegions = [...newRegions, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        });
        const regionCountries = allRegions.data.filter(i => filteredRegions.includes(i.id)).map(r => r.countries);
        let newCountries = [];
        for (let i = 0; i < regionCountries.length; i++) {
            // @ts-expect-error can be undefined
            newCountries = newCountries.concat(regionCountries[i]);
        }
        setRegions(filteredRegions);
        setCountries(newCountries);
    }, [setRegions, regions, allRegions, setCountries]);

    const onRegionRemove = useCallback((index: number) => {
        if (regions && allRegions) {
            const newRegions = [...regions];
            if (newRegions[index]) {
                newRegions.splice(index, 1)
            }
            setRegions(newRegions);
            const regionCountries = allRegions.data.filter(i => newRegions.includes(i.id)).map(r => r.countries);
            let newCountries = [];
            for (let i = 0; i < regionCountries.length; i++) {
                // @ts-expect-error can be undefined
                newCountries = newCountries.concat(regionCountries[i]);
            }
            setCountries(newCountries);
        }
    }, [regions, allRegions, setRegions, setCountries]);

    const changeCountries = useCallback((s: string | number) => {
        const newCountries = countries ? [...countries] : [];
        setCountries([...newCountries, typeof s === 'string' ? parseInt(s) : s].filter((value, index, array) => {
            return array.indexOf(value) === index;
        }));
    }, [setCountries, countries]);

    const onSelectAllCountries = useCallback(() => {
        if (!listCountries)
            return;
        setCountries(listCountries.data.map(i => i.id));
    }, [listCountries, setCountries]);

    useEffect(() => {
        setTitle(t('admin.users.new.button'));
        dispatch(setSidebarTitle(t('admin.users.new.button')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendRolesRequest();
                sendUsersRequest();
                sendAllowedRolesRequest();
                sendRegionsRequest();
                sendCountriesRequest();
            }).catch()
        }
        dispatch(clear())
    }, [setSidebarTitle, dispatch, sendRolesRequest, sendAllowedRolesRequest, sendUsersRequest, user, sendRegionsRequest, sendCountriesRequest]);

    const formUI = useMemo(() => {
        if (!system || !system.system || !roles || !users || isForbidden || !user || !user.user)
            return <></>;

        return <form onSubmit={submit}>
            <OutlineInput name={t('admin.users.table.first_name')} value={firstName} fieldName="first_name"
                          validated={validated}
                          onChange={changeFirstName}/>
            <OutlineInput className="mt-4" name={t('admin.users.table.last_name')} value={lastName}
                          fieldName="last_name" validated={validated}
                          onChange={changeLastName}/>
            <OutlineInput className="mt-4" name={t('admin.users.table.email')} value={email} fieldName="email"
                          type="email"
                          validated={validated} onChange={changeEmail}/>
            {id ? <div className="max-w-md mt-4">
                <div className="relative flex w-full">
                    <Input success={!hasError('password', errors) && validated}
                           error={validated && hasError('password', errors)} crossOrigin=''
                           variant={'outlined'} label={t('admin.users.table.password')} type={passwordType}
                           onChange={changePassword}
                           value={password}
                           className="inline-block dark:text-gray-500 dark:border-blue-gray-800 dark:placeholder-shown:border-blue-gray-800 dark:focus:border-blue-gray-800 dark:focus:border-t-transparent"
                           labelProps={{className: "dark:after:border-blue-gray-800 dark:before:border-blue-gray-800 dark:peer-focus:text-white dark:peer-focus:before:!border-blue-gray-800 dark:peer-focus:after:!border-blue-gray-800"}}
                           icon={
                               <FontAwesomeIcon onClick={() => generatePassword()} icon={solid('shield-halved')}
                                                className={`w-5 h-5 cursor-pointer ${passwordType === 'text' ? 'text-teal-500' : 'text-blue-gray-900 dark:text-white'}`}/>
                           }
                    />
                </div>

                {validated && hasError('password', errors) &&
                    <label key={'error-password'}
                           className="mt-1 text-red-500 text-xs block">{getError('password', errors)}</label>}
            </div> : ''}
            {roles.data.find(r => r.id === role)?.name !== 'Admin' ?
                <OutlineInput className="mt-4" name={t('admin.users.table.domain')} value={domain} fieldName="domain"
                              validated={validated}
                              onChange={changeDomain}/> : ''}
            <OutlineInput className="mt-4" name={t('admin.users.table.ips')} value={ips} fieldName="ips"
                          validated={validated}
                          onChange={changeIps}/>

            {![1, null].includes(roles.data.find(r => r.id === role)?.parent_id ?? null) ? <>
                <div className="max-w-md mt-4">
                    <DropdownInput
                        buttonClassName="min-h-[auto!important]"
                        name={t('admin.users.table.reported')}
                        className="w-full min-w-[200px]"
                        selected={parent ?? 0}
                        onChange={changeParent} list={parentUsers()}/>
                </div>
            </> : ''}
            {role !== 1 ? <>
                <div className="max-w-md mt-4">
                    <DropdownInput
                        buttonClassName="min-h-[auto!important]"
                        id="regions_form"
                        name={t('admin.payment_methods.regions')}
                        className="mb-4"
                        searchable={true}
                        multiple={true}
                        selected={regions}
                        onChange={changeRegions}
                        onRemove={onRegionRemove}
                        list={allRegions?.data ?? []}/>
                </div>

                <div className="max-w-md mt-4">
                    <DropdownInput
                        buttonClassName="min-h-[auto!important]"
                        name={t('admin.payment_methods.table.countries')}
                        searchable={true}
                        multiple={true}
                        withAllButton={true}
                        selected={countries}
                        onChange={changeCountries}
                        onRemove={onCountryRemove}
                        onSelectAll={onSelectAllCountries}
                        onClear={onCountryClear}
                        list={listCountries?.data.map((item) => {
                            return {
                                id: item.id,
                                name: item.name,
                                icon: <CountryAvatar country={item} withName={false}
                                                     className="h-3 max-w-[1rem]"/>
                            }
                        }) ?? []}/>

                    {validated && hasError('countries', errors) &&
                        <label key={'error-countries'}
                               className="mt-1 text-red-500 text-xs block">{getError('countries', errors)}</label>}
                </div>
            </> : ''}

            <div className="max-w-md mt-4">
                <DropdownInput
                    name={t('admin.users.table.timezone')}
                    className="w-full min-w-[200px]"
                    selected={timezone}
                    searchable={true}
                    onChange={changeTimezone}
                    list={system.system.timezones.map((t) => {
                        return {id: t.id, name: t.name, icon: <div className="text-gray-400 mr-6">{t.hours}</div>}
                    })}
                />
                {validated && hasError('timezone', errors) &&
                    <label key={'error-timezone'}
                           className="mt-1 text-red-500 text-xs block">{getError('timezone', errors)}</label>}
            </div>

            {user.user.permissions.includes('update base currency') ? <div className="max-w-md mt-4">
                <DropdownInput name={t('admin.users.table.currency')} className="w-full min-w-[200px]"
                               selected={baseCurrency}
                               searchable={true}
                               onChange={changeCurrency}
                               list={system.system.currencies.filter(c => !c.isCrypto).map((c) => {
                                   return {id: c.id, name: c.code};
                               })}/>
                {validated && hasError('currency', errors) &&
                    <label key={'error-currency'}
                           className="mt-1 text-red-500 text-xs block">{getError('currency', errors)}</label>}
            </div> : ''}

            <div className="max-w-md mt-4">
                <div className="w-full grid grid-cols-3 gap-2 userform-flex">
                    <DropdownInput
                        buttonClassName="min-w-[auto!important]"
                        // className="w-[calc(33% - 2.5rem)] max-w-[9.3rem]"
                        //            menuClassName="max-w-[9.3rem] min-w-[9.3rem]"
                        name={t('admin.users.table.status')}
                        selected={status}
                        onChange={changeStatus} list={system.system.user_statuses}/>
                    <DropdownInput
                        // className="w-[calc(33% - 2.5rem)] max-w-[9.3rem]"
                        //            menuClassName="max-w-[9.3rem] min-w-[9.3rem]"
                        buttonClassName="min-w-[auto!important]"
                        name={t('admin.users.role')}
                        selected={role}
                        onChange={changeRole} list={roles.data}/>
                    <DropdownInput
                        // className="w-[calc(33% - 2.5rem)] max-w-[9.3rem]"
                        //            menuClassName="max-w-[9.3rem] min-w-[9.3rem]"
                        buttonClassName="min-w-[auto!important]"
                        name={t('admin.users.table.language')}
                        selected={language}
                        onChange={changeLanguage} list={system.system.languages.map((i) => {
                        return {
                            ...i,
                            icon: <CountryAvatar country={i} withName={false} className="h-3 max-w-[1rem]"/>
                        }
                    })}/>
                </div>
                {validated && hasError('status', errors) &&
                    <label key={'error-status'}
                           className="mt-1 text-red-500 text-xs block">{getError('status', errors)}</label>}
                {validated && hasError('role', errors) &&
                    <label key={'error-role'}
                           className="mt-1 text-red-500 text-xs block">{getError('role', errors)}</label>}

                {validated && hasError('language', errors) &&
                    <label key={'error-language'}
                           className="mt-1 text-red-500 text-xs block">{getError('language', errors)}</label>}
            </div>
            {roles.data.find(r => r.id === role)?.name === 'Broker' ? <>
                <div className="max-w-md mt-4">
                    <OutlineInput fieldName='notify_url' value={notifyURL} name={t('admin.profile.notify_url')}
                                  type="text"
                                  validated={validated} onChange={onNotifyUrlChanged} className="col-span-2"/>
                </div>
                <div className="max-w-md mt-4">
                    <OutlineInput fieldName='redirect_url' value={redirectURL}
                                  name={t('admin.profile.redirect_url')}
                                  type="text"
                                  validated={validated}
                                  onChange={onRedirectUrlChanged} className="col-span-2"/>
                </div>
                <div className="max-w-md mt-4">
                    <div className="mb-4">
                        {validated && hasError('swift', errors) ? <label key={'error-swift'}
                                                                         className="mb-1 text-red-500 text-xs block">{getError('swift', errors)}</label>
                            : <label className="mb-1">{t('admin.profile.swift')}</label>}
                        <Editor
                            textareaName="swift"
                            apiKey='q5h9i6jwjwjl6n0zk2kqjv26xaxl6m8zf8ifadjn4ey83tv5'
                            onInit={(_evt, editor) => swiftEditorRef.current = editor}
                            initialValue={nonEuropeWire}
                            init={{
                                height: 500,
                                menubar: false,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'code', 'wordcount'
                                ],
                                toolbar: 'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | blocks | bullist numlist outdent indent',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                            }}
                        />
                    </div>
                    <div className="mb-4">
                        {validated && hasError('sepa', errors) ? <label key={'error-sepa'}
                                                                        className="mt-1 text-red-500 text-xs block">{getError('sepa', errors)}</label>
                            : <label className="mb-1">{t('admin.profile.sepa')}</label>}
                        <Editor
                            textareaName="sepa"
                            apiKey='q5h9i6jwjwjl6n0zk2kqjv26xaxl6m8zf8ifadjn4ey83tv5'
                            onInit={(_evt, editor) => sepaEditorRef.current = editor}
                            initialValue={europeWire}
                            init={{
                                height: 500,
                                menubar: false,
                                plugins: [
                                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                    'insertdatetime', 'media', 'table', 'code', 'wordcount'
                                ],
                                toolbar: 'bold italic forecolor | alignleft aligncenter ' +
                                    'alignright alignjustify | blocks | bullist numlist outdent indent',
                                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                            }}
                        />
                    </div>
                </div>
            </> : ''}
            <div className="mt-12">
                <Button onClick={() => submit()} type="submit" className="text-white bg-teal-500"
                        disabled={isInProgress}>{id ? t('admin.buttons.update') : t('admin.buttons.create')}</Button>
            </div>
        </form>
    }, [system, timezone, user, isForbidden, baseCurrency, parentUsers, notifyURL, europeWire,
        nonEuropeWire, redirectURL, errors, validated, isInProgress, password, id, parent, passwordType,
        firstName, lastName, status, domain, email, ips, roles, language, role, generatePassword,
        changeCurrency, countries, regions, onCountryRemove, onRegionRemove, changeCountries, changeRegions,
        listCountries, allRegions, onSelectAllCountries]);

    return (
        <PageBlock>
            {!formIsReady ? <FormSkeleton/> : <>
                <Card className="page-card-block mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                    <CardBody className="overflow-auto px-0">
                        {formUI}
                    </CardBody>
                </Card>
            </>}
        </PageBlock>
    );
}
export default memo(UserForm);
